import { useState, forwardRef, useRef } from "react";
import {
  FormControl,
  FormErrorMessage,
  Box,
  Input,
  InputProps,
  FormLabel,
  Flex,
  FlexProps,
} from "@chakra-ui/react";
import { LayoutGroup, motion, Transition } from "framer-motion";

interface FieldProps {
  error?: string;
  label?: string;
  containerStyle?: FlexProps;
}

export type KYDInputProps = InputProps &
  FieldProps & {
    min?: number | string;
    max?: number | string;
  };

const MotionFlex = motion<FlexProps>(Flex);
const MotionLabel = motion(FormLabel);
const MotionBox = motion(Box);

const KYDInput = forwardRef<HTMLInputElement, KYDInputProps>(
  ({ name, label, error, min, max, type, containerStyle, ...props }, ref) => {
    const [focus, setFocus] = useState(false);
    const currentValue =
      props.value !== undefined ? props.value : (props.defaultValue ?? "");

    const hasValue = currentValue !== "";
    const active = focus || hasValue;

    return (
      <FormControl isInvalid={!!error} w="100%">
        <Box
          h="50px"
          boxShadow="0px 0px 10px 0px #0000000D"
          border="1px solid #E6E6E6"
          borderRadius="8px"
          p={3}
          py={active ? 1 : 3}
          transition="border .08s"
          {...containerStyle}
        >
          <Flex
            direction={active ? "column" : "row"}
            align={active ? "stretch" : "center"}
            gap={active ? 0 : 2}
          >
            {label && (
              <FormLabel
                htmlFor={name}
                fontSize={active ? "12px" : "14px"}
                fontWeight={active ? "400" : "500"}
                color="#00000080"
                whiteSpace={active ? "normal" : "nowrap"}
                mb={0}
                mr={1}
                pointerEvents="none"
                display="flex"
                alignItems="center"
              >
                {label}
              </FormLabel>
            )}

            <Box flex="1" display="flex" alignItems="center">
              <Input
                ref={ref}
                id={name}
                name={name}
                type={type}
                variant="unstyled"
                fontFamily="Helvetica"
                fontSize="14px"
                _placeholder={{ color: "#00000080" }}
                _focus={{ outline: "none", boxShadow: "none" }}
                onFocus={() => setFocus(true)}
                onChange={props.onChange}
                {...props}
                onBlur={(e) => {
                  setFocus(false);
                  props.onBlur?.(e);
                }}
              />
            </Box>
          </Flex>
        </Box>
        {error && <FormErrorMessage fontSize="12px">{error}</FormErrorMessage>}
      </FormControl>
    );
  }
);

KYDInput.displayName = "KYDInput";

export default KYDInput;
